import AudioEngine from 'scratch-audio';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';
import bindAll from 'lodash.bindall';
import {connect} from 'react-redux';
import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import ReactModal from 'react-modal';

import MwGUIComponent from '../components/mw-gui/mw-gui.jsx';
import {processHrefParams} from '../lib/bp_microworlds_util.js';

import {setTopicProjectSettings, setTopicProjectFromLoad} from '../reducers/topic-project';
import {setIsSubmitted, setIsEditable, setBpDataSettings} from '../reducers/bp-data';
import {updateAllToolboxes, setToolboxFromLoad} from '../reducers/toolbox';
import {setMicroworldsSettings, setMicroworldsFromLoad} from '../reducers/microworlds';
import {openTipsLibrary} from '../reducers/modals';
import {activateDeck} from '../reducers/cards';
import {setPlayer, setViewer} from '../reducers/mode';

import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import ProjectLoaderHOC from '../lib/project-loader-hoc.jsx';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';


class MwGUI extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'updateAndLoadProject',
            'updateMicroworldsProperties',
            'updateStateAndProjectData',
            '_inIframe'
        ]);
        this.state = {
            loading: !props.vm.initialized,
            loadingError: false,
            errorMessage: ''
        };
    }
    componentDidMount () {
        if (this.props.vm.initialized) return;
        this.audioEngine = new AudioEngine();
        this.props.vm.attachAudioEngine(this.audioEngine);

        this.updateAndLoadProject(this.props.projectData)
            .then(() => {
                this.setState({loading: false}, () => {
                    this.props.vm.setCompatibilityMode(true);
                    this.props.vm.start();
                });
            })
            .catch(e => {
                // Need to catch this error and update component state so that
                // error page gets rendered if project failed to load
                this.setState({loadingError: true, errorMessage: e});
            });
        this.props.vm.initialized = true;
    }
    componentWillReceiveProps (nextProps) {
        if (this.props.projectData !== nextProps.projectData ||
            this.props.hrefParams !== nextProps.hrefParams) {

            this.setState({loading: true}, () => {
                this.updateAndLoadProject(nextProps.projectData)
                    .then(() => {
                        this.setState({loading: false});
                    })
                    .catch(e => {
                        // Need to catch this error and update component state so that
                        // error page gets rendered if project failed to load
                        this.setState({loadingError: true, errorMessage: e});
                    });
            });
        }
    }
    updateAndLoadProject (projectData) {
        const updatedProjectData = this.updateStateAndProjectData(projectData);

        // if we're in one of the "view modes", don't ask for approval before leaving.
        if (this.props.hrefParams.full_screen === 'true' || this.props.hrefParams.viewer_mode === 'true') {
            window.onbeforeunload = null;
        }
        return this.props.vm.loadProject(updatedProjectData);
    }
    updateMicroworldsProperties (projectData) {
        // If the project file includes the toolbox setup, extract it and
        // update the state to show it.
        const parsedProjectData = projectData ? JSON.parse(projectData) : null;

        if (parsedProjectData) {
            if (parsedProjectData.mwSettings) {
                const mwSettings = parsedProjectData.mwSettings;

                // Update microworlds settings.
                this.props.setStateMicroworldsSettings(mwSettings);

                // Update the toolboxes from the project file
                if (mwSettings.projectToolbox) {
                    this.props.callUpdateAllToolboxes(mwSettings.projectToolbox);
                }

                // Activate onboarding deck
                if (mwSettings.hasOwnProperty('onboardingDeckId')) {
                    this.props.onActivateDeckFactory(mwSettings.onboardingDeckId);
                }
            } else if (parsedProjectData.mwState) {
                this.props.setStateToolboxFromLoad(parsedProjectData.toolboxState);
                this.props.setStateMwFromLoad(parsedProjectData.mwState, parsedProjectData.tpState);
            }

            return parsedProjectData;
        }
    }
    updateStateAndProjectData (projectData) {
        const parsedProjectData = this.updateMicroworldsProperties(projectData);

        const hrefSettings =
            this.props.hrefParams ? processHrefParams(this.props.hrefParams) : {};

        if (hrefSettings && hrefSettings.bpDataHref) {
            this.props.setStateBpDataSettings(hrefSettings.bpDataHref);
        }

        const topicProjectSettings = hrefSettings ? hrefSettings.topicProjectHref : null;
        if (topicProjectSettings) {
            topicProjectSettings.projectId = this.props.projectId;

            // For some projects, we want to append assets api calls that will be called for everytopic.
            // These are defined in the microworlds settings, but are attached to topicProjectSettings here.
            if (parsedProjectData && parsedProjectData.mwSettings &&
                    parsedProjectData.mwSettings.projectSpriteLibraryURLS) {
                const projectSpriteLibraryURLS = parsedProjectData.mwSettings.projectSpriteLibraryURLS;
                if (topicProjectSettings.spriteLibraryURLS) {
                    topicProjectSettings.spriteLibraryURLS =
                        projectSpriteLibraryURLS.concat(topicProjectSettings.spriteLibraryURLS);
                }
            }

            this.props.setStateTopicProjectSettings(topicProjectSettings);
            this.props.setStateIsSubmitted(this.props.isSubmitted);
            this.props.setStateIsEditable(this.props.isEditable);

            if (topicProjectSettings.playerMode) {
                this.props.setStatePlayer(topicProjectSettings.playerMode);
            }
            if (topicProjectSettings.viewerMode) {
                this.props.setStateViewer(topicProjectSettings.viewerMode);
            }
        }
        return projectData;
    }
    _inIframe () {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }
    render () {
        if (this.state.loadingError) throw new Error(`Failed to load project: ${this.state.errorMessage}`);
        if (process.env.NODE_ENV === 'production' && !this._inIframe()) throw new Error(`Not loaded inside of an iframe`);
        const {
            children,
            fetchingProject,
            loadingStateVisible,
            /* eslint-disable  no-unused-vars */
            projectData,
            callUpdateAllToolboxes,
            hrefParams,
            isEditingTargetStage,
            isSubmitted,
            isEditable,
            projectId,
            projectSpriteLibraryURLS,
            onActivateDeckFactory,
            setStateBpDataSettings,
            setStateIsSubmitted,
            setStateIsEditable,
            setStateMicroworldsSettings,
            setStateMwFromLoad,
            setStatePlayer,
            setStateTopicProjectSettings,
            setStateToolboxFromLoad,
            setStateViewer,
            /* eslint-enable  no-unused-vars */
            vm,
            ...componentProps
        } = this.props;
        return (
            <MwGUIComponent
                handleLoadProject={this.updateAndLoadProject}
                isMw={this.props.isMw}
                loading={fetchingProject || this.state.loading || loadingStateVisible}
                vm={vm}
                {...componentProps}
            >
                {children}
            </MwGUIComponent>
        );
    }
}

MwGUI.propTypes = {
    ...MwGUIComponent.propTypes,
    fetchingProject: PropTypes.bool,
    importInfoVisible: PropTypes.bool,
    isSubmitted: PropTypes.bool,
    isEditable: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    previewInfoVisible: PropTypes.bool,
    projectData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    vm: PropTypes.instanceOf(VM)
};

MwGUI.defaultProps = MwGUIComponent.defaultProps;

const mapStateToProps = state => ({
    activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
    blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
    cardsVisible: state.scratchGui.cards.visible,
    costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
    importInfoVisible: state.scratchGui.modals.importInfo,
    isMw: state.scratchGui.microworlds.isMw,
    isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
    loadingStateVisible: state.scratchGui.modals.loadingProject,
    localSave: state.scratchGui.topicProject.localSave,
    onboardingDeckId: state.scratchGui.microworlds.onboardingDeckId,
    viewerMode: state.scratchGui.mode.viewerMode,
    soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
    theme: state.scratchGui.topicProject.theme,
    tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
    projectSpriteLibraryURLS: state.scratchGui.microworlds.projectSpriteLibraryURLS
});

const mapDispatchToProps = dispatch => ({
    callUpdateAllToolboxes: allToolboxes => dispatch(updateAllToolboxes(allToolboxes)),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onActivateDeckFactory: id => dispatch(activateDeck(id)),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    setStateMicroworldsSettings: mwSettings => dispatch(setMicroworldsSettings(mwSettings)),
    setStateTopicProjectSettings: tpSettings => dispatch(setTopicProjectSettings(tpSettings)),
    setStateBpDataSettings: bpData => dispatch(setBpDataSettings(bpData)),
    setStateIsSubmitted: isSubmitted => dispatch(setIsSubmitted(isSubmitted)),
    setStateIsEditable: isEditable => dispatch(setIsEditable(isEditable)),
    setStateMwFromLoad: (mwState, tpState) => {
        if (mwState) dispatch(setMicroworldsFromLoad(mwState));
        if (tpState) dispatch(setTopicProjectFromLoad(tpState));
    },
    setStatePlayer: isPlayerOnly => dispatch(setPlayer(isPlayerOnly)),
    setStateViewer: isViewer => dispatch(setViewer(isViewer)),
    setStateToolboxFromLoad: toolboxState => dispatch(setToolboxFromLoad(toolboxState))
});

const ConnectedMwGUI = connect(
    mapStateToProps,
    mapDispatchToProps,
)(MwGUI);

const WrappedGui = ErrorBoundaryHOC('Top Level App')(
    ProjectLoaderHOC(vmListenerHOC(ConnectedMwGUI), MwGUI)
);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
